<!-- Main Layout Container with Light Gray Background -->
<div class="h-screen overflow-hidden bg-sidebar">
  <!-- Sidebar at background level -->
  <app-sidebar></app-sidebar>

  <!-- Main Content Card Overlay -->
  <div class="absolute inset-0 flex">
    <!-- Spacer for sidebar width -->
    <div
      [style.width.px]="sidebarService.width()"
      [style.transition]="sidebarService.isResizing() ? 'none' : 'width 300ms ease-in-out'"
      class="shrink-0 max-md:!w-[72px] sidebar-spacer"
    ></div>

    <!-- Main Content Area -->
    <div class="flex-1 flex flex-col min-w-0 min-h-0 px-2 sm:px-4">
      <!-- Main Content Card -->
      <main class="flex-1">
        <div
          hlmCard
          class="relative overflow-hidden flex flex-col my-2 !py-0 !gap-0 h-[calc(100vh-1rem)]"
        >
          <!-- Dot field behind the page header only (guide: never behind content). -->
          <app-dot-field
            class="absolute top-0 right-0 w-1/2 h-44 rounded-tr-lg"
            fadeFrom="top-right"
            color="var(--primary)"
          ></app-dot-field>

          <div
            aria-hidden="true"
            class="pointer-events-none absolute top-0 right-0 w-1/2 h-44 rounded-tr-lg ks-glass-2 [mask-image:radial-gradient(120%_130%_at_100%_0%,#000_0%,rgba(0,0,0,0.6)_45%,transparent_82%)] [-webkit-mask-image:radial-gradient(120%_130%_at_100%_0%,#000_0%,rgba(0,0,0,0.6)_45%,transparent_82%)]"
          ></div>

          <!-- Single scroll region: header stays sticky at its top, so content
               scrolls up underneath the frosted-glass header instead of stopping
               at a separate box below it. -->
          <div class="ks-page-scroll relative z-10 flex-1 overflow-y-auto" [class.flex]="fullBleed()" [class.flex-col]="fullBleed()">
            <!-- Card Header -->
            <div hlmCardHeader class="md:sticky top-0 z-10 border-b border-border !px-0 pt-4 !pb-4 ks-glass-2">
              <div class="ks-page-container col-start-1 row-start-1 flex flex-wrap items-center gap-y-2">
                @if (headerBackAction(); as backAction) {
                  <button
                    hlmBtn
                    type="button"
                    variant="ghost"
                    size="icon"
                    class="!size-8 shrink-0 rounded-md text-muted-foreground hover:text-foreground"
                    [attr.aria-label]="backAction.ariaLabel"
                    (click)="backAction.onClick()"
                  >
                    <ng-icon name="lucideArrowLeft" class="size-4" />
                  </button>
                  <span aria-hidden="true" class="mx-2 h-9 w-px shrink-0 bg-border"></span>
                }
                <div class="min-w-0 flex-1 basis-48">
                  <div class="font-mono text-2xs tracking-wide text-muted-foreground mb-0.5">
                    @if (breadcrumb()) {
                      {{ breadcrumb()!.section }} / {{ breadcrumb()!.page }}
                      @if (headerBackAction() && headerBreadcrumbLabel() !== breadcrumb()!.page) {
                        / {{ headerBreadcrumbLabel() }}
                      }
                    } @else {
                      {{ headerBreadcrumbLabel() }}
                    }
                  </div>
                  <div class="flex min-w-0 flex-wrap items-center gap-1.5">
                    <h1 hlmLead class="!mt-0 truncate font-bold text-foreground">
                      {{ headerTitle() }}
                    </h1>
                    @if (currentOrganization.access()?.accessType === 'platform_admin') {
                      <span class="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground" title="Kenergy-Mitarbeiterzugriff — keine Organisationsmitgliedschaft. Zugriffe werden protokolliert.">Systemadmin</span>
                    }
                    @for (action of headerTitleActions(); track action.label) {
                      <button
                        type="button"
                        class="inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                        [attr.aria-label]="action.ariaLabel ?? action.label"
                        [attr.title]="action.label"
                        [disabled]="action.disabled"
                        (click)="action.onClick()"
                      >
                        @if (action.icon) {
                          <ng-icon [name]="action.icon" class="size-3.5" />
                        }
                      </button>
                    }
                  </div>
                  @if (headerSubtitle(); as subtitle) {
                    <p class="mt-1 text-sm font-normal text-foreground">{{ subtitle }}</p>
                  }
                  @if (headerDescription(); as description) {
                    <p class="mt-0.5 text-xs text-muted-foreground">{{ description }}</p>
                  }
                  @if (headerBadges().length > 0) {
                    @if (compactHeaderBadges()) {
                      <div class="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-2xs text-muted-foreground">
                        @for (badge of headerBadges(); track badge.label + badge.value) {
                          <span class="inline-flex items-baseline gap-1.5 whitespace-nowrap">
                            <span class="text-muted-foreground/60">{{ badge.label }}</span>
                            @if (badge.value) {
                              <span [class]="badge.className ?? ''">{{ badge.value }}</span>
                            }
                          </span>
                        }
                      </div>
                    } @else {
                      <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
                        @for (badge of headerBadges(); track badge.label + badge.value) {
                          <span
                            [class]="'inline-flex items-center gap-1 rounded-lg border border-border/80 bg-muted/60 px-2 py-0.5 text-xs shadow-sm ' + (badge.className ?? '')"
                          >
                            <span class="font-bold">{{ badge.label }}</span>
                            @if (badge.value) {
                              <span [class.font-mono]="badge.monospace">{{ badge.value }}</span>
                            }
                            @if (badge.href) {
                              <a
                                [href]="badge.href"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="ml-0.5 inline-flex size-3.5 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                                [attr.aria-label]="badge.label + ' öffnen'"
                                [attr.title]="badge.label + ' öffnen'"
                              >
                                <ng-icon name="lucideExternalLink" class="size-3" />
                              </a>
                            }
                          </span>
                        }
                      </div>
                    }
                  }
                </div>

                <div class="ml-auto flex max-w-full flex-wrap items-center gap-2">
                  @if (headerActionsTemplate() || headerActions.actions().length > 0) {
                    @if (headerActionsTemplate(); as actionsTemplate) {
                      <ng-container [ngTemplateOutlet]="actionsTemplate" />
                    }
                    @for (action of headerActions.actions(); track action.label) {
                      <button
                        hlmBtn
                        [variant]="action.variant ?? 'ghost'"
                        [size]="action.iconOnly ? 'icon' : 'sm'"
                        [class.!size-7]="action.iconOnly"
                        [class.!h-7]="!action.iconOnly"
                        class="rounded-md"
                        [attr.aria-label]="action.ariaLabel ?? action.label"
                        [disabled]="action.disabled"
                        (click)="action.onClick()"
                      >
                        @if (action.icon) {
                          <ng-icon [name]="action.icon" class="size-4 shrink-0"></ng-icon>
                        }
                        @if (!action.iconOnly) {
                          {{ action.label }}
                        }
                      </button>
                    }
                  }

                  @if (currentOrganization.organizationId()) {
                    <button
                      hlmBtn
                      variant="ghost"
                      size="icon"
                      class="!size-8 rounded-lg text-muted-foreground hover:text-foreground"
                      aria-label="Hilfe anfragen"
                      title="Hilfe anfragen"
                      (click)="supportDialog.open('help')"
                    >
                      <ng-icon name="lucideCircleHelp" class="size-4 text-muted-foreground"></ng-icon>
                    </button>
                    <button
                      hlmBtn
                      variant="ghost"
                      size="icon"
                      class="!size-8 rounded-lg text-muted-foreground hover:text-foreground"
                      aria-label="Feedback senden"
                      title="Feedback senden"
                      (click)="supportDialog.open('feedback')"
                    >
                      <ng-icon name="lucideMessageSquarePlus" class="size-4 text-muted-foreground"></ng-icon>
                    </button>
                  }

                  <div class="relative">
                    <button
                      hlmBtn
                      variant="ghost"
                      size="icon"
                      class="!size-8 rounded-lg text-muted-foreground hover:text-foreground"
                      [attr.aria-label]="'Aktivität' + (notificationStore.unreadCount() ? ', ' + notificationStore.unreadCount() + ' ungelesen' : '') + (notificationStore.runningCount() ? ', ' + notificationStore.runningCount() + ' laufend' : '')"
                      (click)="notificationStore.toggleSheet()"
                    >
                      <ng-icon name="lucideBell" class="size-4 text-muted-foreground"></ng-icon>
                    </button>
                    @if (notificationStore.unreadCount() > 0) {
                      <span class="pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border-[1.5px] border-background bg-danger px-1 font-mono text-2xs leading-none text-white" aria-hidden="true">{{ notificationStore.unreadCount() > 9 ? '9+' : notificationStore.unreadCount() }}</span>
                    } @else if (notificationStore.runningCount() > 0) {
                      <span class="pointer-events-none absolute -right-0.5 -top-0.5 size-2 animate-pulse rounded-full border-[1.5px] border-background bg-primary" aria-hidden="true"></span>
                    }
                  </div>
                </div>
              </div>

              <!-- Page tabs, pinned with the header so they stay put while the
                   content scrolls. Pages declare them via PageTabsService. Sits
                   in the grid's second row — no divider against the title row,
                   the tabs read as part of one header rather than a second bar. -->
              @if (pageTabs.tabs().length > 0) {
                <div class="ks-page-container col-start-1 row-start-2 -mb-4 mt-3 overflow-x-auto">
                  <nav class="flex min-w-max gap-6" role="tablist" aria-label="Seitenbereiche">
                    @for (tab of pageTabs.tabs(); track tab.value) {
                      <button
                        type="button"
                        role="tab"
                        [attr.aria-selected]="pageTabs.active() === tab.value"
                        [attr.aria-disabled]="tab.disabled || null"
                        [attr.title]="tab.disabledReason ?? null"
                        [disabled]="tab.disabled"
                        (click)="pageTabs.select(tab.value)"
                        class="relative inline-flex h-11 items-center gap-1.5 text-sm font-normal transition-colors disabled:cursor-not-allowed disabled:text-muted-foreground/35"
                        [class]="
                          pageTabs.active() === tab.value
                            ? 'text-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        "
                      >
                        {{ tab.label }}
                        @if (tab.count !== undefined) {
                          <span
                            class="rounded-full bg-muted px-1.5 py-0.5 font-mono text-2xs leading-none text-muted-foreground"
                            [ngClass]="pageTabs.active() === tab.value ? 'bg-primary/10 text-primary' : ''"
                          >
                            {{ tab.count }}
                          </span>
                        }
                        @if (pageTabs.active() === tab.value) {
                          <span class="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-primary"></span>
                        }
                      </button>
                    }
                  </nav>
                </div>
              }

              <app-notification-sheet />
              <app-support-dialog #supportDialog />
            </div>

            <!-- Main Content -->
            <div hlmCardContent [class]="fullBleed() ? 'flex min-h-[36rem] min-w-0 flex-1 flex-col !p-0' : 'ks-page-container pt-5 pb-6'" [class.ks-page-editing]="saveBar.isEditing()">
              <!-- Router Outlet for Dynamic Content -->
              <router-outlet></router-outlet>
            </div>
          </div>

          <!-- Fixed save bar. Sits in the outer hlmCard (relative, overflow-hidden),
               not inside the scroll region, so it stays pinned to the bottom of
               the card regardless of how far a page's own content scrolls.
               Pages opt in via PageSaveBarService (register a saver + beginEdit()). -->
          @if (saveBar.isEditing()) {
            <div
              class="ks-page-scroll ks-glass-2 absolute inset-x-0 bottom-0 z-20 overflow-y-auto border-t border-border py-3"
            >
              <div class="ks-page-container flex flex-wrap items-center justify-between gap-3">
                <span class="min-w-0 truncate text-xs text-muted-foreground">
                  @if (saveBar.error()) {
                    <span role="alert" class="font-normal text-destructive">{{ saveBar.error() }}</span>
                  } @else if (saveBar.isDirty()) {
                    <span class="inline-flex items-center gap-1.5"><span class="size-1.5 rounded-full bg-warning" aria-hidden="true"></span>Ungespeicherte Änderungen <kbd class="hidden rounded border border-border px-1 font-mono text-2xs sm:inline">⌘S</kbd></span>
                  } @else {
                    Keine Änderungen
                  }
                </span>

                <div class="flex flex-none items-center gap-2">
                  <button
                    type="button"
                    (click)="saveBar.cancel()"
                    [disabled]="saveBar.isSaving()"
                    class="h-9 rounded-lg border border-border bg-background px-4 text-dense font-normal text-muted-foreground hover:bg-accent disabled:opacity-50"
                  >
                    Verwerfen
                  </button>
                  <button
                    type="button"
                    (click)="saveBar.save()"
                    [disabled]="saveBar.isSaving() || !saveBar.isDirty()"
                    class="h-9 rounded-lg bg-primary px-5 text-dense font-bold text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    {{ saveBar.isSaving() ? 'Wird gespeichert …' : 'Speichern' }}
                  </button>
                </div>
              </div>
            </div>
          }
        </div>
      </main>
    </div>
  </div>
</div>
